import { useState, useEffect } from "react";
import { collection, doc, getDoc, getDocs, query, where } from "firebase/firestore";
import { db } from "../firebase";
import { POSITIONS } from "../constants";
import { calcStars, getWeeklyChallenge } from "../utils";
import { Diamond } from "./Diamond";

function PositionSelect({ info, onSelect, onSignOut, onViewDashboard, onPlayScenarios, onViewPlays, isGuest, userProfile }) {
  const [hover,setHover]=useState(null);
  const [posStats,setPosStats]=useState({});
  const [assignedScenarios,setAssignedScenarios]=useState([]);
  const challenge=getWeeklyChallenge();
  const assignedPositions=userProfile?.assignedPositions||[];
  const assignedScenarioIds=userProfile?.assignedScenarioIds||[];
  const dailyStreak=userProfile?.dailyStreak||0;

  useEffect(()=>{
    if(!userProfile?.id||isGuest)return;
    getDocs(query(collection(db,"playLogs"),where("userId","==",userProfile.id))).then(snap=>{
      const stats={};
      snap.docs.forEach(d=>{
        const{position,correct}=d.data();
        if(!stats[position])stats[position]={correct:0,total:0};
        stats[position].total++;
        if(correct)stats[position].correct++;
      });
      setPosStats(stats);
    }).catch(()=>{});
  },[userProfile?.id,isGuest]);

  useEffect(()=>{
    if(isGuest||assignedScenarioIds.length===0){setAssignedScenarios([]);return;}
    Promise.all(assignedScenarioIds.map(id=>getDoc(doc(db,"customScenarios",id))))
      .then(snaps=>setAssignedScenarios(snaps.filter(s=>s.exists()).map(s=>({id:s.id,...s.data()}))))
      .catch(()=>setAssignedScenarios([]));
  },[isGuest,assignedScenarioIds.join(",")]);// eslint-disable-line

  return (
    <div style={{padding:"16px 18px 24px"}}>
      <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12}}>
        <div>
          <div style={{fontSize:14,fontWeight:700,color:"#231F20"}}>Pick your position</div>
          <div style={{fontSize:11,color:"#999"}}>{info.innings} innings · {info.level.charAt(0).toUpperCase()+info.level.slice(1)} level</div>
        </div>
        <div style={{display:"flex",gap:6}}>
          {!isGuest&&onViewPlays&&<button onClick={onViewPlays} style={{fontSize:10,color:"#856404",background:"#fff8e1",border:"1px solid #ffe082",borderRadius:5,padding:"4px 9px",cursor:"pointer",fontWeight:600}}>📋 Team Plays</button>}
          {!isGuest&&onViewDashboard&&<button onClick={onViewDashboard} style={{fontSize:10,color:"#231F20",background:"#e3fbe6",border:"1px solid #8fe39c",borderRadius:5,padding:"4px 9px",cursor:"pointer",fontWeight:600}}>📊 My Progress</button>}
          <button onClick={onSignOut} style={{fontSize:10,color:"#bbb",background:"none",border:"1px solid #e0e0e0",borderRadius:5,padding:"3px 9px",cursor:"pointer"}}>{isGuest?"✕ Exit":"Sign Out"}</button>
        </div>
      </div>

      {!isGuest&&(
        <div style={{display:"flex",gap:8,marginBottom:12}}>
          <div style={{flex:1,background:"#fff8e1",border:"1px solid #ffe082",borderRadius:8,padding:"8px 10px",display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:18}}>📅</span>
            <div>
              <div style={{fontSize:9,color:"#856404",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>Daily Streak</div>
              <div style={{fontSize:18,fontWeight:900,fontFamily:"monospace",color:"#856404"}}>{dailyStreak>0?`${dailyStreak} 🔥`:"0 days"}</div>
            </div>
          </div>
          <div style={{flex:1,background:"#e3fbe6",border:"1px solid #8fe39c",borderRadius:8,padding:"8px 10px",display:"flex",alignItems:"center",gap:8}}>
            <span style={{fontSize:18}}>🎯</span>
            <div>
              <div style={{fontSize:9,color:"#231F20",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.05em"}}>This Week</div>
              <div style={{fontSize:11,fontWeight:700,color:"#231F20"}}>5 in a row at {challenge.label}</div>
            </div>
          </div>
        </div>
      )}

      {assignedPositions.length>0&&(
        <div style={{background:"#1FD71D",borderRadius:8,padding:"8px 12px",marginBottom:12,display:"flex",alignItems:"center",gap:8,flexWrap:"wrap"}}>
          <span style={{fontSize:11,fontWeight:700,color:"#231F20"}}>📋 Coach assigned:</span>
          {assignedPositions.map(p=><span key={p} style={{background:"#231F20",color:"#1FD71D",fontSize:10,fontWeight:700,padding:"2px 8px",borderRadius:999,fontFamily:"monospace"}}>{p}</span>)}
        </div>
      )}

      {assignedScenarios.length>0&&(
        <div style={{background:"#fff8e1",border:"1.5px solid #1FD71D",borderRadius:8,padding:"10px 12px",marginBottom:12}}>
          <div style={{display:"flex",alignItems:"center",justifyContent:"space-between",flexWrap:"wrap",gap:8}}>
            <span style={{fontSize:11,fontWeight:700,color:"#856404"}}>🎯 Coach assigned {assignedScenarios.length} scenario{assignedScenarios.length!==1?"s":""} for you</span>
            <button onClick={()=>onPlayScenarios?.(assignedScenarios)} style={{padding:"6px 14px",background:"#231F20",color:"#1FD71D",border:"none",borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:700,fontFamily:"monospace"}}>Play Now →</button>
          </div>
        </div>
      )}

      <div style={{maxWidth:290,margin:"0 auto 14px"}}>
        <Diamond mode="select" onPosClick={onSelect} highlightPos={hover} size={290}/>
      </div>

      <div style={{display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:6,maxWidth:300,margin:"0 auto"}}>
        {POSITIONS.map(p=>{
          const s=posStats[p.id], stars=s?calcStars(s.correct,s.total):0;
          const isAssigned=assignedPositions.includes(p.id);
          return (
            <button key={p.id} onClick={()=>onSelect(p)} onMouseEnter={()=>setHover(p.id)} onMouseLeave={()=>setHover(null)}
              style={{padding:"7px 4px",background:hover===p.id?"#231F20":isAssigned?"#fffde7":"white",border:isAssigned?"2px solid #1FD71D":"1px solid #e0e0e0",borderRadius:6,cursor:"pointer",fontSize:11,fontWeight:700,color:hover===p.id?"#1FD71D":"#333",fontFamily:"monospace",transition:"all 0.15s",textAlign:"center"}}>
              {p.id}
              <div style={{fontSize:8,marginTop:2,color:hover===p.id?"#1FD71D":"#1FD71D",letterSpacing:0}}>
                {"★".repeat(stars)}{"☆".repeat(5-stars)}
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}

export default PositionSelect;
